import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Save, X, Package, Tag } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Prodotti = () => {
  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ nome: '', sigla: '' });

  useEffect(() => {
    fetchProdotti();
  }, []);

  const fetchProdotti = async () => {
    try {
      const response = await axios.get(`${API}/prodotti`);
      setProdotti(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei prodotti');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.sigla || formData.sigla.length < 1) {
      toast.error('La sigla è obbligatoria');
      return;
    }
    
    try {
      await axios.post(`${API}/prodotti`, {
        nome: formData.nome,
        sigla: formData.sigla.toUpperCase()
      });
      toast.success('Prodotto creato con successo');
      fetchProdotti();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleDelete = async (nome) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo prodotto?')) return;
    try {
      await axios.delete(`${API}/prodotti/${encodeURIComponent(nome)}`);
      toast.success('Prodotto eliminato con successo');
      fetchProdotti();
    } catch (error) {
      toast.error('Errore durante eliminazione');
    }
  };

  const resetForm = () => {
    setFormData({ nome: '', sigla: '' });
    setShowForm(false);
  };

  // Auto-genera sigla dalle prime lettere del nome
  const handleNomeChange = (value) => {
    setFormData({ 
      ...formData, 
      nome: value,
      // Auto-genera sigla se vuota
      sigla: formData.sigla || value.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 4)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="loading-indicator">
        <div className="w-8 h-8 border-4 border-[#FFE500] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="prodotti-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-[#FFE500] p-2 rounded-xl">
            <Package className="w-5 h-5 text-[#0A1628]" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white uppercase tracking-tight">Tipi di Prodotto</h2>
            <p className="text-sm text-white/50">Gestisci descrizioni e sigle dei prodotti</p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold uppercase tracking-wide text-xs transition-colors ${
            showForm 
              ? 'bg-white/10 text-white border border-white/20 hover:bg-white/20' 
              : 'bg-[#FFE500] text-[#0A1628] shadow-lg shadow-[#FFE500]/20 hover:bg-[#FFE500]/90'
          }`}
          data-testid="add-prodotto-btn"
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? 'Annulla' : 'Nuovo Prodotto'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5" data-testid="prodotto-form">
          <h3 className="text-lg font-bold text-white uppercase tracking-tight mb-4">Nuovo Prodotto</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1 uppercase">
                  Descrizione Prodotto *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => handleNomeChange(e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                  required
                  placeholder="es. VONGOLE VERACI, COZZE NOSTRANE..."
                  data-testid="prodotto-nome-input"
                />
                <p className="text-[10px] text-white/40 mt-1">Nome completo del prodotto</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1 uppercase">
                  Sigla *
                </label>
                <input
                  type="text"
                  value={formData.sigla}
                  onChange={(e) => setFormData({ ...formData, sigla: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFE500] uppercase font-bold tracking-wider"
                  required
                  maxLength={6}
                  placeholder="es. VV, CN, OS..."
                  data-testid="prodotto-sigla-input"
                />
                <p className="text-[10px] text-white/40 mt-1">Sigla breve (max 6 caratteri) - usata nei PDF e report</p>
              </div>
            </div>
            
            {/* Preview */}
            {formData.nome && formData.sigla && (
              <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                <p className="text-xs text-white/50 mb-2">Anteprima:</p>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg font-bold">
                    <Tag className="w-4 h-4" />
                    {formData.sigla}
                  </span>
                  <span className="text-white">{formData.nome}</span>
                </div>
              </div>
            )}
            
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-[#FFE500] text-[#0A1628] rounded-xl font-bold uppercase tracking-wide text-xs shadow-lg shadow-[#FFE500]/20 hover:bg-[#FFE500]/90 transition-colors"
              data-testid="save-prodotto-btn"
            >
              <Save className="w-4 h-4" />
              Salva Prodotto
            </button>
          </form>
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3">
        <p className="text-xs text-blue-400">
          <strong>Nota:</strong> La sigla viene mostrata nei PDF, nei report e nello stato bins per identificare rapidamente il tipo di prodotto.
        </p>
      </div>

      {/* Tabella */}
      <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl overflow-hidden" data-testid="prodotti-table-container">
        <table className="text-sm w-full border-collapse" data-testid="prodotti-table">
          <thead>
            <tr className="bg-[#FFE500]/10">
              <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center w-24">
                Sigla
              </th>
              <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-left">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Descrizione Prodotto
                </div>
              </th>
              <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center w-24">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {prodotti.length === 0 ? (
              <tr>
                <td colSpan="3" className="text-center py-8 text-white/50" data-testid="no-prodotti-message">
                  Nessun prodotto presente. Crea il primo tipo di prodotto (es. VONGOLE, COZZE...).
                </td>
              </tr>
            ) : (
              prodotti.map((prodotto, index) => (
                <tr key={prodotto.nome} className="border-b border-white/10 hover:bg-white/5 transition-colors" data-testid={`prodotto-row-${index}`}>
                  <td className="py-3 px-4 text-center">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-500/20 text-blue-400 rounded-lg font-bold text-sm">
                      <Tag className="w-3 h-3" />
                      {prodotto.sigla || prodotto.nome.slice(0, 3).toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-medium text-white">{prodotto.nome}</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <button
                      onClick={() => handleDelete(prodotto.nome)}
                      className="p-1.5 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                      data-testid={`delete-prodotto-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Prodotti;
